import type { AgentSummary } from '@/hooks/useAIAgents';

import {
  formatLocalDateTimeInput,
  localDateTimeInputToISOString,
} from './dateTimeLocal';

// The backend caps hunt duration at 3 days (see MaxDurationFromNow in
// backend/pkg/services/hunt/service.go). Keep the default and the
// picker max identical to the server-side bound so users can't queue
// up a request the server will reject.
export const MAX_DURATION_DAYS = 3;
export const DEFAULT_AGENT_NAME = 'hannibal';

const MAX_DURATION_MS = MAX_DURATION_DAYS * 24 * 60 * 60 * 1000;

export interface HuntFormState {
  prompt: string;
  finishCriteria: string;
  guardrails: string;
  expiresAt: string;
  agentName: string;
  // `undefined` ⇒ accept the agent's full tool set. An explicit array
  // narrows to a strict subset; we only send `allowedTools` to the
  // server when it's a true subset (see resolveAllowedTools).
  toolSelection: string[] | undefined;
}

function daysFromNow(days: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
}

export function defaultExpiresAt(): string {
  return formatLocalDateTimeInput(daysFromNow(MAX_DURATION_DAYS));
}

export function maxExpiresAt(): string {
  return formatLocalDateTimeInput(daysFromNow(MAX_DURATION_DAYS));
}

export function initialHuntFormState(): HuntFormState {
  return {
    prompt: '',
    finishCriteria: '',
    guardrails: '',
    expiresAt: defaultExpiresAt(),
    agentName: DEFAULT_AGENT_NAME,
    toolSelection: undefined,
  };
}

/**
 * Returns the strict tool subset to send as `allowedTools`, or
 * `undefined` for "use agent default" (server payload omits the field).
 * An empty selection or a selection equal to the full available set both
 * collapse to default — keeping the wire format minimal and preserving
 * forward-compat if the agent adds new tools.
 */
export function resolveAllowedTools(
  selection: string[] | undefined,
  available: string[]
): string[] | undefined {
  if (!selection || selection.length === 0) return undefined;
  if (selection.length === available.length) return undefined;
  return selection;
}

/**
 * A hunt root agent must be a capability-mode agent that accepts a Hunt
 * target. Filtering client-side keeps the /agents contract generic.
 */
export function isHuntRootAgent(agent: AgentSummary): boolean {
  return Boolean(agent.capability) && (agent.target ?? []).includes('hunt');
}

/**
 * Returns a user-facing error string when `value` (the raw datetime-local
 * input value) is unparseable, in the past, or further than
 * MAX_DURATION_DAYS in the future. Returns null when the value is valid.
 * Mirrors the server-side validateExpiresAt in
 * backend/pkg/services/hunt/service.go.
 */
export function validateExpiresAtInput(value: string): string | null {
  if (!value) return null;
  let expires: number;
  try {
    expires = new Date(localDateTimeInputToISOString(value)).getTime();
  } catch {
    return 'Invalid expiry.';
  }
  const now = Date.now();
  if (expires <= now) return 'Expiry must be in the future.';
  if (expires - now > MAX_DURATION_MS) {
    return `Expiry must be within ${MAX_DURATION_DAYS} days.`;
  }
  return null;
}
